import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Users, UserCircle, FileText, Menu, X, Leaf, Map, LogOut, Settings,
  Receipt, PieChart, CalendarDays, MessageSquare, Megaphone, ClipboardList, Clock,
  ChevronDown, UserPlus, BookOpen, DollarSign, AlertTriangle, MessageCircle, Shield, Wallet,
  FolderTree, TrendingUp, UserCog, BarChart3, FileSpreadsheet
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import CommunicationLogger from './CommunicationLogger';
import './Layout.css';

// Nav group definitions
// - ownerOnly: only visible to business owners (Sarah)
// - adminOrOwner: visible to admin-role employees and business owners
// - managerHidden: hidden from users with the 'manager' role
const NAV_GROUPS = [
  {
    id: 'sales',
    label: 'Sales',
    items: [
      { path: '/admin/estimates', icon: FileText, label: 'Estimates & Leads' },
      { path: '/admin/clients', icon: Users, label: 'Clients' },
      { path: '/admin/analytics', icon: BarChart3, label: 'Sales Analytics', managerHidden: true },
    ]
  },
  {
    id: 'scheduling',
    label: 'Scheduling',
    items: [
      { path: '/admin/dispatch', icon: Map, label: 'Dispatch' },
      { path: '/admin/calendar', icon: CalendarDays, label: 'Calendar' },
      { path: '/admin/team-availability', icon: Users, label: 'Team Availability' },
      { path: '/admin/todos', icon: ClipboardList, label: 'To-Do List' },
    ]
  },
  {
    id: 'time-pay',
    label: 'Time & Pay',
    items: [
      { path: '/admin/time-cards', icon: Clock, label: 'Time Cards' },
      { path: '/admin/gusto-report', icon: FileSpreadsheet, label: 'Gusto Report', ownerOnly: true },
      { path: '/admin/attendance', icon: AlertTriangle, label: 'Attendance Points' },
    ]
  },
  {
    id: 'people',
    label: 'People',
    items: [
      { path: '/admin/employees', icon: UserCircle, label: 'Employees' },
      { path: '/admin/onboarding', icon: UserPlus, label: 'Onboarding' },
      { path: '/admin/quizzes', icon: BookOpen, label: 'Quizzes & Training' },
      { path: '/admin/complaints-feedback', icon: MessageCircle, label: 'Complaints & Feedback' },
    ]
  },
  {
    id: 'accounting',
    label: 'Accounting',
    items: [
      { path: '/admin/invoices', icon: Receipt, label: 'Invoices', adminOrOwner: true },
      { path: '/admin/expenses', icon: Wallet, label: 'Expenses & Income', ownerOnly: true },
      { path: '/admin/cashflow', icon: TrendingUp, label: 'Cashflow & P&L', ownerOnly: true },
      { path: '/admin/accounts', icon: Wallet, label: 'Accounts', ownerOnly: true },
      { path: '/admin/expense-categories', icon: FolderTree, label: 'Expense Categories', ownerOnly: true },
      { path: '/admin/tips', icon: DollarSign, label: 'Tips Report', ownerOnly: true },
    ]
  },
  {
    id: 'comms',
    label: 'Communication',
    items: [
      { path: '/admin/messages', icon: MessageSquare, label: 'Client Messages' },
      { path: '/admin/staff-messages', icon: Megaphone, label: 'Staff Messages' },
    ]
  }
];

const OWNER_EMAILS = [
  'sarah@twosisterscleaningco.com',
  'sarah.keel@twosisterscleaningco.com',
];

function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, logout } = useAuth();
  const { getAdminUnreadCount, state } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  const isOwner = user?.email && OWNER_EMAILS.includes(user.email.toLowerCase());

  // Detect 'manager' role from the employee record so we can hide financial/reporting nav
  const currentEmployee = user?.email
    ? state.employees.find(emp => emp.email?.toLowerCase() === user.email.toLowerCase())
    : null;
  const isManager = currentEmployee?.role === 'manager';
  const isAdmin = currentEmployee?.role === 'admin' || currentEmployee?.role === 'owner';

  // An item is visible if it's not owner-only (or user is owner),
  // not admin-or-owner (or user is admin/owner),
  // AND not manager-hidden (or user is not manager)
  const canSeeItem = (item) =>
    (!item.ownerOnly || isOwner) &&
    (!item.adminOrOwner || isOwner || isAdmin) &&
    (!item.managerHidden || !isManager);

  const unreadMessages = getAdminUnreadCount();
  
  const pendingAck = state.internalMessages?.filter(m => 
    m.requiresAcknowledgment && 
    m.acknowledgedBy?.length < state.employees.filter(e => e.status === 'active').length
  ).length || 0;

  const pendingQuoteRequests = state.estimates?.filter(e => 
    e.status === 'pending_review'
  ).length || 0;
  
  // New leads count
  const newLeadCount = state.estimates?.filter(e => 
    e.status === 'pending_review' || e.leadStatus === 'new'
  ).length || 0;

  // Open complaints count
  const openComplaints = state.complaints?.filter(c => c.status === 'open').length || 0;

  // Badge map by path
  const badges = {
    '/admin/estimates': newLeadCount || pendingQuoteRequests,
    '/admin/messages': unreadMessages,
    '/admin/staff-messages': pendingAck,
    '/admin/complaints-feedback': openComplaints,
  };
  const badgeColors = {
    '/admin/estimates': 'orange',
    '/admin/complaints-feedback': 'red',
  };

  // Determine which group contains the active route
  const getActiveGroup = () => {
    for (const g of NAV_GROUPS) {
      if (g.items.some(i => location.pathname.startsWith(i.path))) return g.id;
    }
    return null;
  };
  
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const toggleGroup = (groupId) => {
    setCollapsedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleViewAsCleaner = () => {
    localStorage.removeItem('viewAsCleaner');
    navigate('/team?preview=cleaner', { replace: false });
  };

  return (
    <div className="layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        {/* Logo */}
        <div className="sidebar-header">
          <div className="logo">
            <div className="logo-icon"><Leaf size={24} /></div>
            {sidebarOpen && (
              <div className="logo-text">
                <span className="logo-title">Keel Group</span>
                <span className="logo-subtitle">Operations</span>
              </div>
            )}
          </div>
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {/* Dashboard — always top-level */}
          <NavLink to="/admin/dashboard" className={({ isActive }) => `nav-link top-level ${isActive ? 'active' : ''}`}>
            <LayoutDashboard size={20} />
            {sidebarOpen && <span>Dashboard</span>}
          </NavLink>

          {/* Owner Dashboard — only for business owners */}
          {isOwner && (
            <NavLink to="/admin/owner-dashboard" className={({ isActive }) => `nav-link top-level ${isActive ? 'active' : ''}`}>
              <Shield size={20} />
              {sidebarOpen && <span>Owner Reports</span>}
            </NavLink>
          )}

          {/* Grouped nav items */}
          {sidebarOpen ? (
            NAV_GROUPS.map(group => {
              const visibleItems = group.items.filter(canSeeItem);
              if (visibleItems.length === 0) return null;
              const isCollapsed = collapsedGroups[group.id];
              const isActiveGroup = group.id === getActiveGroup();
              return (
                <div key={group.id} className="nav-group">
                  <button
                    className={`nav-group-header ${isCollapsed ? 'collapsed' : ''} ${isActiveGroup ? 'active-group' : ''}`}
                    onClick={() => toggleGroup(group.id)}
                  >
                    <span>{group.label}</span>
                    <ChevronDown size={14} className="nav-group-chevron" />
                  </button>
                  <div className={`nav-group-items ${isCollapsed ? 'collapsed' : ''}`}>
                    {visibleItems.map(item => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={({ isActive }) => `nav-link grouped ${isActive ? 'active' : ''}`}
                      >
                        <item.icon size={18} />
                        <span>{item.label}</span>
                        {badges[item.path] > 0 && (
                          <span className={`nav-badge ${badgeColors[item.path] || ''}`}>
                            {badges[item.path] > 99 ? '99+' : badges[item.path]}
                          </span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            // Collapsed sidebar — just show icons for all items
            NAV_GROUPS.flatMap(g => g.items).filter(canSeeItem).map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                title={item.label}
              >
                <item.icon size={20} />
                {badges[item.path] > 0 && (
                  <span className={`nav-badge ${badgeColors[item.path] || ''}`}>
                    {badges[item.path] > 99 ? '99+' : badges[item.path]}
                  </span>
                )}
              </NavLink>
            ))
          )}

          {/* Reports & Settings — bottom */}
          <div className="nav-spacer" />
          {!isManager && (
            <NavLink to="/admin/reports" className={({ isActive }) => `nav-link top-level ${isActive ? 'active' : ''}`}>
              <PieChart size={20} />
              {sidebarOpen && <span>Reports</span>}
            </NavLink>
          )}
          <NavLink to="/admin/settings" className={({ isActive }) => `nav-link top-level ${isActive ? 'active' : ''}`}>
            <Settings size={20} />
            {sidebarOpen && <span>Settings</span>}
          </NavLink>
        </nav>

        <div className="sidebar-footer">
          {sidebarOpen && user && (
            <div className="user-info">
              <span className="user-email">{user.email}</span>
            </div>
          )}
          <button
            className="view-as-btn"
            onClick={handleViewAsCleaner}
            title="View as Cleaner"
          >
            <UserCog size={20} />
            {sidebarOpen && <span>View as Cleaner</span>}
          </button>
          <button className="logout-btn" onClick={handleLogout} title="Sign Out">
            <LogOut size={20} />
            {sidebarOpen && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      <main className="main-content">
        <Outlet />
      </main>

      <CommunicationLogger />
    </div>
  );
}

export default Layout;
